import { useQuery } from "@tanstack/react-query";
import {
  getAdminConversionAPI,
  getAdminOverviewAPI,
  getAdminRetentionAPI,
  getAdminSummaryAPI,
  getAdminTransactionsAPI,
} from "../../api/dashbordApi";
import { queryKeys } from "../keys";
import { getTokens } from "../../utils/tokens";

const KPI_REFRESH_MS = 5 * 60 * 1000;

const getMonthMeta = (date = new Date()) => ({
  month: date.getMonth() + 1,
  year: date.getFullYear(),
});

export function useAdminPlatformKpiQuery() {
  const hasToken = !!getTokens()?.access_token;

  return useQuery({
    queryKey: queryKeys.admin.platformKpi,
    enabled: hasToken,
    staleTime: KPI_REFRESH_MS,
    gcTime: KPI_REFRESH_MS * 2,
    refetchInterval: KPI_REFRESH_MS,
    queryFn: async () => {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const currentMeta = getMonthMeta(now);
      const prevMeta = getMonthMeta(prev);

      const [
        overviewResponse,
        currentSummary,
        previousSummary,
        conversionResponse,
        retentionResponse,
        transactionsResponse,
      ] = await Promise.all([
        (getAdminOverviewAPI as any)({}),
        (getAdminSummaryAPI as any)(currentMeta),
        (getAdminSummaryAPI as any)(prevMeta),
        (getAdminConversionAPI as any)({}),
        (getAdminRetentionAPI as any)({}),
        (getAdminTransactionsAPI as any)({}),
      ]);

      const currentGross = Number(currentSummary?.gross || 0);
      const previousGross = Number(previousSummary?.gross || 0);

      const revenueDelta =
        previousGross > 0
          ? ((currentGross - previousGross) / previousGross) * 100
          : null;

      const ageSource =
        transactionsResponse?.pendingPayoutsAge ||
        transactionsResponse?.payoutsPendingAge ||
        transactionsResponse?.pendingAge ||
        null;

      return {
        kpis: overviewResponse?.kpis || null,
        conversion: conversionResponse || null,
        retention: retentionResponse || null,
        revenueDelta,
        revenuePrevMonth: previousGross,
        pendingAges: {
          over24h:
            ageSource?.over24h ??
            ageSource?.gt24h ??
            ageSource?.olderThan24h ??
            null,
          over72h:
            ageSource?.over72h ??
            ageSource?.gt72h ??
            ageSource?.olderThan72h ??
            null,
        },
      };
    },
  });
}
